import { useAtomValue } from 'jotai';
import { useCallback } from 'react';
import { ERole } from '@/shared/lib/roles';
import { isRouteAuthorized } from '@/shared/lib/routePermissions';
import {
  controllerPermissionsDerivedAtom,
  userRoleDerivedAtom,
} from '@/shared/store/atoms/sessionAtom';

/**
 * Hook de acesso às permissões de controller do usuário autenticado.
 *
 * Usuários com role `Master` têm acesso irrestrito a tudo.
 *
 * Fornece checagem por `controllerId`, `controllerName` e por pathname de rota.
 *
 * Expõe também os papéis, porque parte do gating não é por controller: várias
 * telas herdadas do legado mostram ou escondem ação por papel (`Secretary` não
 * escreve, `Master` vê o que ninguém mais vê). Os nomes vêm do `roles` do login,
 * a mesma lista que `EUserRole` descreve e que {@link ERole} nomeia.
 *
 * @example
 * ```tsx
 * const { hasController, isRouteAllowed, isMaster, hasRole } = usePermissions();
 *
 * if (!hasController(EController.Exam)) return null; // bloqueia feature de exam
 * if (!isRouteAllowed('/persons')) return null;
 * if (hasRole(ERole.Secretary)) return null;         // esconde ação de escrita
 * ```
 */
export function usePermissions() {
  const controllers = useAtomValue(controllerPermissionsDerivedAtom);
  const roles = useAtomValue(userRoleDerivedAtom);

  const isMaster = roles?.includes(ERole.Master) ?? false;

  /**
   * O usuário tem este papel?
   *
   * **`Master` não passa por aqui.** Ele tem acesso irrestrito a controller, mas
   * papel é identidade, não permissão: um Master não é secretária, e responder
   * `true` para todo papel faria a checagem de `Secretary` esconder ação dele.
   *
   * O parâmetro é o enum, não `string`: com a string solta, um `hasRole('Secretaria')`
   * compilava e devolvia `false` para sempre — uma restrição que nunca se aplica
   * e não falha em lugar nenhum.
   *
   * @param name - Papel a verificar (ex.: `ERole.Secretary`).
   */
  const hasRole = useCallback((name: ERole): boolean => roles?.includes(name) ?? false, [roles]);

  /**
   * Verifica se o usuário tem permissão para um controller específico.
   *
   * @param idOrName - ID numérico ou nome string do controller
   */
  const hasController = useCallback(
    (idOrName: number | string): boolean => {
      if (isMaster) return true;
      return controllers.some(c =>
        typeof idOrName === 'number' ? c.controllerId === idOrName : c.controllerName === idOrName,
      );
    },
    [isMaster, controllers],
  );

  const isRouteAllowed = useCallback(
    (pathname: string): boolean => {
      if (isMaster) return true;
      return isRouteAuthorized(pathname, controllers);
    },
    [isMaster, controllers],
  );

  return { controllers, hasController, isRouteAllowed, isMaster, hasRole } as const;
}
